import * as THREE from 'three'
import * as sketch from './sketch.js'
import * as planes from './planes.js'
import * as corner from './corner.js'
import * as ruin from './ruin.js'

// 'place'  = tap drops/moves the ruin (Phase 1)
// 'sketch' = tap-to-start / tap-to-stop polyline drawing (Phase 2A)
// 'ruin'   = 3-tap input → procedural ruined wall corner (Phase 3 Option 2)
const MODE_PLACE = 'place'
const MODE_SKETCH = 'sketch'
const MODE_RUIN = 'ruin'

let _session = null
let _closing = false
let _renderer = null
let _hitTestSource = null
let _localSpace = null
let _viewerSpace = null
let _modelPlaced = false
// Ruin is the headline mode (3-tap → procedural ruined wall corner).
// Place + Sketch are secondary tools accessed via the mode toggle.
let _mode = MODE_RUIN
let _onPlace = null
let _onSessionEnd = null
let _onHitStateChange = null
let _onModeChange = null
let _onPlanesChanged = null
let _onRuinStateChange = null
let _onDemoModeChange = null
let _demoMode = false
let _hitState = null
let _firstHitLogged = false
let _selectCount = 0
let _lastHitMatrix = null
let _lastHitPosition = new THREE.Vector3()
let _hasHitThisFrame = false
let _lastPlaneCountKey = ''

export async function initAR({
  scene,
  camera,
  renderer,
  reticle,
  modelGroup,
  onPlace,
  onSessionEnd,
  onHitStateChange,
  onModeChange,
  onPlanesChanged,
  onRuinStateChange,
  onDemoModeChange
}) {
  if (!navigator.xr) throw new Error('WebXR not available')

  _renderer = renderer
  _onPlace = onPlace
  _onSessionEnd = onSessionEnd
  _onHitStateChange = onHitStateChange
  _onModeChange = onModeChange
  _onPlanesChanged = onPlanesChanged
  _onRuinStateChange = onRuinStateChange
  _onDemoModeChange = onDemoModeChange
  _demoMode = false
  _lastPlaneCountKey = ''
  _hitState = null
  _firstHitLogged = false
  _selectCount = 0
  _lastHitMatrix = null
  _hasHitThisFrame = false
  _mode = MODE_RUIN
  sketch.initSketch(scene)
  planes.initPlanes(scene)
  corner.initCorner(scene)
  ruin.initRuin(scene)

  const session = await navigator.xr.requestSession('immersive-ar', {
    // 'local' is implicit for immersive-ar per spec, but listing it explicitly
    // avoids ambiguity. We deliberately do NOT ask for 'local-floor' — many
    // ARCore devices can't establish a floor-stage reference, and three.js's
    // default is 'local-floor' which trips the requestReferenceSpace error.
    requiredFeatures: ['local', 'hit-test', 'dom-overlay'],
    domOverlay: { root: document.getElementById('ar-overlay') },
    // 'plane-detection' is optional so older Chrome / non-ARCore devices
    // can still place the ruin via hit-test alone. When present, planes.js
    // visualizes detected planes; Phase 3 builds the procedural ruin from
    // them.
    optionalFeatures: ['light-estimation', 'plane-detection']
  })

  _session = session
  _modelPlaced = false

  // Override three.js's default 'local-floor' so its internal
  // requestReferenceSpace call uses 'local' (matches what we use below).
  renderer.xr.setReferenceSpaceType('local')
  await renderer.xr.setSession(session)

  _localSpace = await session.requestReferenceSpace('local')
  _viewerSpace = await session.requestReferenceSpace('viewer')
  _hitTestSource = await session.requestHitTestSource({ space: _viewerSpace })

  // Some browsers only dispatch 'select' to the XR controller, not the session.
  // Wire up both to be safe.
  const controller = renderer.xr.getController(0)
  scene.add(controller)
  controller.addEventListener('select', () => onSelect('controller', reticle, modelGroup))
  session.addEventListener('select', () => onSelect('session', reticle, modelGroup))
  session.addEventListener('end', handleSessionEnd)

  renderer.setAnimationLoop((_ts, frame) => {
    onXRFrame(frame, reticle, modelGroup)
    // Without this call three.js never draws the scene — only the AR
    // compositor's camera passthrough is visible.
    renderer.render(scene, camera)
  })

  return session
}

export function getSession() {
  return _session
}

export async function endSession() {
  if (!_session || _closing) return
  _closing = true
  try {
    await _session.end()
  } catch (err) {
    // Harmless race: the session was already ending (system back gesture
    // / Chrome's exit-AR badge / a duplicate ghost-click on the ✕
    // button). The 'end' event will still fire and clean up.
    if (err && err.name !== 'InvalidStateError') throw err
  }
}

export function getMode() {
  return _mode
}

export function setMode(next) {
  if (next !== MODE_PLACE && next !== MODE_SKETCH && next !== MODE_RUIN) return
  if (next === _mode) return

  // If we leave sketch mode mid-stroke, commit the active stroke at its
  // current position so we don't end up with dangling state.
  if (_mode === MODE_SKETCH && sketch.isDrawing()) {
    sketch.endStroke(_hasHitThisFrame ? _lastHitPosition : null)
  }

  _mode = next
  if (_onModeChange) _onModeChange(next)
}

export function clearSketches() {
  sketch.clearSketches()
}

export function clearRuin() {
  ruin.clearRuin()
  setDemoMode(false)
  if (_onRuinStateChange) _onRuinStateChange(ruin.getState(), ruin.getHint())
}

export function toggleRuinVisible() {
  ruin.toggleVisible()
}

export function ruinIsBuilt() {
  return ruin.isBuilt()
}

export const Modes = { PLACE: MODE_PLACE, SKETCH: MODE_SKETCH, RUIN: MODE_RUIN }

function setHitState(next) {
  // After first placement, the hint is owned by main.js (showPlacedHint).
  // Don't let hit-test transitions overwrite it.
  if (_modelPlaced) return
  // In ruin mode, ruin.js + handleRuinTap own the hint. Don't let
  // place-mode's "scanning…" / "Tap to place" leak in.
  if (_mode === MODE_RUIN) return
  if (_hitState === next) return
  _hitState = next
  if (_onHitStateChange) _onHitStateChange(next)
}

function onXRFrame(frame, reticle, modelGroup) {
  _hasHitThisFrame = false

  // In demo mode (ruin built) the reticle is just visual noise — hide it.
  if (_demoMode) {
    reticle.visible = false
  }

  if (!frame || !_hitTestSource || !_localSpace) {
    reticle.visible = false
    return
  }

  // Reticle keeps tracking after first placement — subsequent taps move
  // the ruin to the new reticle position.

  const hits = frame.getHitTestResults(_hitTestSource)
  if (hits.length === 0) {
    setHitState('searching')
    reticle.visible = false
    return
  }

  const pose = hits[0].getPose(_localSpace)
  if (!pose) {
    setHitState('searching')
    reticle.visible = false
    return
  }

  if (!_firstHitLogged) {
    _firstHitLogged = true
    console.info('[ar] first hit-test result received')
  }

  setHitState('tracking')
  // Even with a valid hit, suppress the reticle in demo mode.
  reticle.visible = !_demoMode
  reticle.matrix.fromArray(pose.transform.matrix)
  reticle.matrixWorldNeedsUpdate = true

  if (!_lastHitMatrix) _lastHitMatrix = new THREE.Matrix4()
  _lastHitMatrix.fromArray(pose.transform.matrix)
  _lastHitPosition.setFromMatrixPosition(_lastHitMatrix)
  _hasHitThisFrame = true

  // Sketch mode: feed the active stroke a fresh sample every frame.
  if (_mode === MODE_SKETCH && sketch.isDrawing()) {
    sketch.tickStroke(_lastHitPosition)
  }

  // Always refresh detected-plane visualizations. Cheap when nothing
  // changes (just iterates the set and refreshes pose matrices).
  planes.updatePlanes(frame, _localSpace)
  corner.updateCorner(frame, _localSpace)

  // Notify UI when the per-orientation count or corner-availability
  // changes — every-frame calls would thrash the DOM.
  const counts = planes.planeCounts()
  const hasCorner = corner.hasCorner()
  const key = `${counts.horizontal}/${counts.vertical}/${counts.other}/${hasCorner ? 'c' : '-'}`
  if (key !== _lastPlaneCountKey) {
    _lastPlaneCountKey = key
    if (_onPlanesChanged) _onPlanesChanged(counts, hasCorner)
  }
}

function onSelect(source, reticle, modelGroup) {
  _selectCount += 1
  console.info(`[ar] ${source} select #${_selectCount} mode=${_mode}`)

  // Vibration confirms the tap registered even if there's no hit yet to
  // place / sketch against.
  if (navigator.vibrate) {
    try {
      navigator.vibrate(40)
    } catch (_) {
      /* ignore */
    }
  }

  if (_mode === MODE_SKETCH) {
    handleSketchTap()
  } else if (_mode === MODE_RUIN) {
    handleRuinTap()
  } else {
    handlePlaceTap(reticle, modelGroup)
  }
}

function handleRuinTap() {
  if (!_lastHitMatrix) {
    console.info('[ar] ruin tap rejected: no hit-test result yet')
    if (_onRuinStateChange) {
      // Surface the rejection to the user — silent drops are confusing.
      _onRuinStateChange(ruin.getState(), '👇 Point at the floor first — no surface detected')
    }
    return
  }
  const newState = ruin.handleTap(_lastHitPosition)
  console.info(`[ar] ruin tap accepted, new state=${newState}`)
  if (_onRuinStateChange) _onRuinStateChange(newState, ruin.getHint())
  // Once the ruin is fully built, declutter: hide plane outlines,
  // corner marker, reticle, HUD, and the place-mode placeholder.
  if (newState === 'built') setDemoMode(true)
}

function setDemoMode(on) {
  if (_demoMode === on) return
  _demoMode = on
  planes.setVisible(!on)
  corner.setVisible(!on)
  if (_onDemoModeChange) _onDemoModeChange(on)
}

function handlePlaceTap(reticle, modelGroup) {
  if (!_lastHitMatrix) {
    console.info('[ar] place: no hit yet, ignoring')
    return
  }

  // Use the last good hit pose, not reticle.matrix — ARCore can lose the
  // current frame's hit between the user's tap and the select event.
  const pos = new THREE.Vector3()
  const quat = new THREE.Quaternion()
  const scl = new THREE.Vector3()
  _lastHitMatrix.decompose(pos, quat, scl)

  modelGroup.position.copy(pos)
  modelGroup.quaternion.copy(quat)
  modelGroup.visible = true
  modelGroup.updateMatrixWorld(true)

  const wasFirstPlacement = !_modelPlaced
  _modelPlaced = true

  console.info(
    `[ar] ${wasFirstPlacement ? 'placed' : 'moved'} to ${pos
      .toArray()
      .map((n) => n.toFixed(2))}, modelChildren=${modelGroup.children.length}`
  )

  if (_onPlace) _onPlace(wasFirstPlacement)
}

function handleSketchTap() {
  // Same gate as place-tap: don't require a hit *this exact frame* (race
  // with the select event), require any hit ever in this session.
  if (!_lastHitMatrix) {
    console.info('[ar] sketch: no hit yet, ignoring')
    return
  }

  if (sketch.isDrawing()) {
    sketch.endStroke(_lastHitPosition)
    console.info(`[ar] sketch: ended stroke (${sketch.strokeCount()} total)`)
  } else {
    sketch.startStroke(_lastHitPosition)
    console.info('[ar] sketch: started stroke')
  }
}

function handleSessionEnd() {
  // Stop the animation loop so the page is responsive again. Without this,
  // renderer.render(scene, camera) keeps running at 60fps post-session.
  if (_renderer) _renderer.setAnimationLoop(null)

  // Clean up sketch + plane state — the parent scene is GC'd when the next
  // session creates a fresh one, but disposing eagerly avoids leaking GL
  // buffers in the meantime.
  sketch.clearSketches()
  planes.clearPlanes()
  corner.clearCorner()
  ruin.clearRuin()

  _hitTestSource = null
  _localSpace = null
  _viewerSpace = null
  _modelPlaced = false
  _mode = MODE_RUIN
  _session = null
  _renderer = null
  _hitState = null
  _firstHitLogged = false
  _selectCount = 0
  _lastHitMatrix = null
  _hasHitThisFrame = false
  _closing = false
  _demoMode = false
  if (_onSessionEnd) _onSessionEnd()
}
